import Model from './model';
import { InstallStatus, HasSettingsStatus, AppType } from '../constants';
import {
  enableableOsPermissionStatuses,
  osToFitbitPermissionMap,
} from '../config';
import { getLoadingValue } from '../actions/gallery/set-loading-rate-app';
import { IApp } from '../types/app';

export default class App extends Model<IApp> {
  constructor(args: any) {
    super(args);
  }

  static selectFromState(id: string, { gallery, registry, installFlow }: any) {
    if (!id) {
      return;
    }

    const {
      appProgress: { [id]: appProgress },
      settings: { [id]: settings = {} },
      currentlySyncing,
      syncMode,
      connectionStatus: { connected },
      grantedPermissions: { [id]: clientGrantedPermissions = {} },
      selectedClock,
      osPermissions
    } = gallery;

    const {
      apps: { [id]: appData },
      installedApps: { [id]: installed = {} },
      grantedPermissions: { [id]: registryGrantedPermissions = {} },
      pendingAppInstallStatus: { [id]: pendingStatus },
      favorites,
      features: { flags: featureFlags },
      currentUser,
      installed: {
        tileStatuses
      },
      pendingTileInstalls,
      rate_app
    } = registry;

    const uiLoadingRateApp = getLoadingValue(gallery, id);

    const { continueInstall = {} } = installFlow;

    if (!appData) {
      return;
    }

    let tileInstalled = false
    if (appData.type.toLocaleLowerCase() === AppType.TILE && tileStatuses) {
      const installedTileIds = tileStatuses.map((tile: any) => tile.tileId)
      tileInstalled = installedTileIds.includes(appData.id)
    }

    let tilePendingInstall = false
    if (!tileInstalled && appData.type.toLocaleLowerCase() === AppType.TILE && pendingTileInstalls) {
      tilePendingInstall = pendingTileInstalls.includes(appData.id)
    }

    return App.memoizeByKey(id, {
      ...appData,
      progress: appProgress ? appProgress.progress : undefined,
      pendingInstallStatusChange: !!pendingStatus,
      installStatus: installed.installStatus,
      buildId: installed.currentBuildId,
      priority: installed.priority,
      hasSettings: settings.hasSettings || HasSettingsStatus.NO,
      clientGrantedPermissions,
      registryGrantedPermissions,
      currentlySyncing,
      syncMode,
      connected,
      continueInstall,
      favorite: favorites.includes(id),
      active: installed.active,
      selected: installed.selected,
      clientSelected: selectedClock,
      featureFlags,
      currentUser,
      rateAppStatus: rate_app,
      uiLoadingRateApp,
      tileInstalled,
      tilePendingInstall,
      osPermissions
    });
  }

  navigateToSettings = () => {
    const { id, buildId, isClock } = this;

    let returnUrl = `/${isClock ? 'clock' : 'app'}/${id}`;

    const url = window.location.href;
    if (url) {
      const parts = url.split('#');
      if (url.length === 2) {
        returnUrl = parts[1];
      }
    }

    window.__webview_comms__.launch('settings', {
      appId: id,
      buildId,
      returnUrl
    });
  };

  get hasSettings() {
    return this._get('hasSettings') === HasSettingsStatus.YES;
  }

  get isApp() {
    return this._get('type') === 'APP';
  }

  get isClock() {
    return this._get('type') === 'CLOCK';
  }

  get isTile() {
    return this._get('type') === 'TILE';
  }

  get installStatus() {
    return this._get('installStatus') || InstallStatus.NOT_INSTALLED;
  }

  get isRemovable() {
    return this._get('priority') !== 'REQUIRED';
  }

  get versionString() {
    if (!(this.latestVersion && this.latestVersion.versionString)) {
      return undefined;
    }

    return this.latestVersion.versionString;
  }

  get components() {
    if (!(this.latestVersion && this.latestVersion.components)) {
      return [];
    }

    return this.latestVersion.components;
  }

  get hasCompanion() {
    if (!this.components.length) {
      return false;
    }
    for (const component of this.components) {
      if (component.descriptor === 'COMPANION') {
        return true;
      }
    }
    return false;
  }

  // We only care about watch component for now
  get size() {
    if (this.components.length) {
      for (const component of this.components) {
        if (component.descriptor === 'WATCH') {
          return component.size;
        }
      }
    }
    return undefined;
  }

  get updatedDate() {
    if (!(this.latestVersion && this.latestVersion.createdAt)) {
      return undefined;
    }

    const { createdAt } = this.latestVersion;
    return new Date(createdAt);
  }

  get availableForDevice() {
    const currentUser = this._get('currentUser');
    const availability = this._get('availability');

    if (!currentUser || !availability) {
      return false;
    }

    for (const { type } of availability.deviceTypes) {
      if (type === currentUser.device.version) {
        return true;
      }
    }

    return false;
  }

  get hasNoVersionAvailable() {
    return this.isNotInstalled && !this.latestVersion;
  }

  get cannotInstallOnDevice() {
    return (
      (this.isNotInstalled && !this._get('connected')) ||
      (this.isTile && this.isInstalling && !this._get('connected')) ||
      this.isRestrictedForUserRegion()
    );
  }

  hasFlag(flag: string) {
    return (this._get('flags') || []).includes(flag);
  }

  isRestrictedForUserRegion() {
    return this.hasFlag('RESTRICTED_FOR_REGION');
  }

  get showProgress() {
    return (
      this.progress !== undefined &&
      !!this._get('continueInstall').shouldContinue &&
      (this.isPending || this.isStaged)
    );
  }

  get requiresSync() {
    return this.isInstalling;
  }

  get requiresWifi() {
    return !!(this.latestVersion && this.latestVersion.requiresWifi);
  }

  get isStaged() {
    return this.isStagedInstall || this.isStagedUpdate;
  }

  get isPending() {
    return (
      this.pendingInstallStatusChange ||
      this.isPendingInstall ||
      this.isPendingUpdate ||
      this.isTilePendingInstall
    );
  }

  get hasFailed() {
    return this.isFailedInstall || this.isFailedUpdate;
  }

  get hasUpdate() {
    return (
      this.hasUpdateAvailable || this.isPendingUpdate || this.isStagedUpdate
    );
  }

  get isActive() {
    const multiClockEnabled = this._get('featureFlags').allowMultipleClockfaces;
    return multiClockEnabled ? this.active || false : this.isOnDevice;
  }

  get isSelected() {
    const clientSelected = this._get('clientSelected');

    if (clientSelected) {
      return clientSelected === this.id;
    }

    return this.selected || false;
  }

  /*
   * There are multiple install statuses where an app is installed on device
   * ON_DEVICE - Application is installed
   * UPDATE_AVAILABLE - Application is installed and there is an update available
   * PENDING_UPDATE - Application is installed and there is a pending update
   * UPDATE_STAGED - Application is installed and there is an update staged
   */
  get isOnDevice() {
    return this.isInstalled || this.hasUpdate || this.isTileInstalled;
  }

  get isInstalling() {
    return (
      this.isPending || this.isStaged || (this.isSelected && !this.isActive)
    );
  }

  get isInstalledOrInstalling() {
    return this.isInstalled || this.isInstalling || this.hasUpdateAvailable;
  }

  get isLoading() {
    return this.isPending || this.isInstalling || (this.isSelected && !this.isActive);
  }

  get hasUpdateAvailable() {
    return this.installStatus === InstallStatus.UPDATE_AVAILABLE;
  }

  get isStagedInstall() {
    return this.installStatus === InstallStatus.INSTALL_STAGED;
  }

  get isStagedUpdate() {
    return this.installStatus === InstallStatus.UPDATE_STAGED;
  }

  get isPendingInstall() {
    return this.installStatus === InstallStatus.PENDING_INSTALL;
  }

  get isTilePendingInstall() {
    return this._data.tilePendingInstall
  }

  get isPendingUpdate() {
    return this.installStatus === InstallStatus.PENDING_UPDATE;
  }

  get isFailedInstall() {
    return this.installStatus === InstallStatus.INSTALL_FAILED;
  }

  get isFailedUpdate() {
    return this.installStatus === InstallStatus.UPDATE_FAILED;
  }

  get isPendingRemoval() {
    return this.installStatus === InstallStatus.PENDING_REMOVAL;
  }

  get isInstalled() {
    return this.installStatus === InstallStatus.ON_DEVICE;
  }

  get isNotInstalled() {
    return this.installStatus === InstallStatus.NOT_INSTALLED;
  }

  get showRatings() {
    const ratingsEnabled = this._get('featureFlags').enable_ratings;
    return ratingsEnabled && !!this.ratingSummary;
  }

  get canRate() {
    return this._get('canRate') || false;
  }

  get richDescription() {
    return this._get('richDescription');
  }

  get richDescriptionPosition() {
    return this._get('richDescriptionPosition');
  }

  get parentApp() {
    return this._get('parentApp') || undefined;
  }

  get learnMore() {
    return this._get('learnMore');
  }

  get learnMoreDeeplink() {
    return this._get('learnMoreDeeplink');
  }

  get showRatingInput() {
    return (
      this.showRatings && this.canRate && (!this.userRating || this.isRatingNow)
    );
  }

  get showRatingInputV2() {
    return this.showRatings && this.canRate;
  }

  get isRatingNow() {
    const rateAppStatus = this._get('rateAppStatus') || {};
    const uiLoadingRateApp = this._get('uiLoadingRateApp');

    return !!(rateAppStatus.loading || uiLoadingRateApp);
  }

  get grantedPermissions() {
    const clientGrantedPermissions = this._get('clientGrantedPermissions');
    const registryGrantedPermissions = this._get('registryGrantedPermissions');

    // If client permissions exist they are preferred but will be cleared
    // when the server is updated
    return Object.keys(clientGrantedPermissions).length
      ? clientGrantedPermissions
      : registryGrantedPermissions;
  }

  get newPermissions() {
    if (!this.hasUpdateAvailable) {
      // Make sure at least an empty array is always returned
      return [];
    } else if (this.hasGrantedPermissions) {
      const grantedPermissions = this.grantedPermissions;
      // Filter out already granted permissions
      return this.requestedPermissions.filter(
        (permission) => !grantedPermissions.hasOwnProperty(permission)
      );
    } else {
      return this.requestedPermissions;
    }
  }

  get requestedPermissions() {
    const requestedPermissions =
      this.latestVersion && this.latestVersion.requestedPermissions;
    return requestedPermissions || [];
  }

  get hasGrantedPermissions() {
    return (
      this.requestedPermissions.length > 0 &&
      Object.keys(this.grantedPermissions).length > 0
    );
  }

  get arePermissionsReady() {
    const { requestedPermissions, newPermissions } = this;
    return (
      // Has no requested permissions
      !requestedPermissions ||
      requestedPermissions.length === 0 ||
      // Is an update with no new permissions
      (this.hasUpdateAvailable &&
        (!newPermissions || newPermissions.length === 0)) ||
      // Is an already installed clock, so we are switching clocks
      (this.isClock && this.isInstalled)
    );
  }

  get hasPermissionWarning() {
    return this.checkIfOsPermissionsAreDisabled();
  }

  get permissionsWithIssues() {
    const permissionsMap = this.osPermissionsEnableableMap;
    const permissions = [];
    for (const permissionName in permissionsMap) {
      if (permissionsMap[permissionName]) {
        permissions.push(permissionName);
      }
    }
    return permissions;
  }

  get osPermissionsEnableableMap() {
    const osPermissions = this._get('osPermissions');
    const permissionsEnableableMap: { [key: string]: boolean } = {};
    for (const osPermission in osToFitbitPermissionMap) {
      const fitbitPermission = osToFitbitPermissionMap[osPermission];
      permissionsEnableableMap[fitbitPermission] =
        this.requestedPermissions.includes(fitbitPermission) &&
        this.grantedPermissions[fitbitPermission] !== false &&
        enableableOsPermissionStatuses.includes(osPermissions[osPermission]);
    }
    return permissionsEnableableMap;
  }

  checkIfOsPermissionsAreDisabled() {
    const osPermissionMap = this.osPermissionsEnableableMap;
    for (const permission in osPermissionMap) {
      if (osPermissionMap[permission]) {
        return true;
      }
    }
    return false;
  }

  get banners() {
    return this._data.banners;
  }

  get buildId() {
    return this._data.buildId;
  }

  get isTileInstalled() {
    return this._data.tileInstalled
  }

  get contactEmail() {
    return this._data.contactEmail;
  }

  get contactUrl() {
    return this._data.contactUrl;
  }

  get developer() {
    return this._data.developer;
  }

  get currentlySyncing() {
    return this._data.currentlySyncing;
  }

  get description() {
    return this._data.description;
  }

  get icon() {
    return this._data.icon;
  }

  get id() {
    return this._data.id;
  }

  get isPaid() {
    return this._data.isPaid;
  }

  get isPrivate() {
    return this._data.isPrivate;
  }

  get latestVersion() {
    return this._data.latestVersion;
  }

  get name() {
    return this._data.name;
  }

  get pendingInstallStatusChange() {
    return this._data.pendingInstallStatusChange;
  }

  get progress() {
    return this._data.progress;
  }

  get syncMode() {
    return this._data.syncMode;
  }

  get type() {
    return this._data.type;
  }

  get favorite() {
    return this._data.favorite;
  }

  get selected() {
    return this._data.selected;
  }

  get active() {
    return this._data.active;
  }

  get userRating() {
    return this._data.userRating;
  }

  get ratingSummary() {
    return this._data.ratingSummary;
  }

  get osPermissions() {
    return this._data.osPermissions;
  }

  get compatibilityMode() {
    return this._data.compatibilityMode;
  }

  get flags() {
    return this._data.flags;
  }

  get isPremium() {
    return this._data.isPremium;
  }

  get isPrimary() {
    return this._data.isPrimary;
  }

  get hidePermissions() {
    return this._data.hidePermissions;
  }

  get bypassPrivateAppWarning() {
    return this._data.bypassPrivateAppWarning;
  }
}
